import moment from 'moment';
import { nanoid } from 'nanoid';

import { CurrencyMajor, CurrencyMinor } from '@fox/atoms/src/inputs/currency-input';
import { tryParseJSONObject } from '@fox/atoms/utils/string';
import { FinancialConfigCommon, INTERVAL_SHORT, Interval } from './contracts';

export const intervalDisplay = (interval: string, units?: number | null) => `${units && units > 1 ? `${units} ` : ''}${interval}`;

export const intervalDisplayShort = (interval: Interval, units?: number | null) =>
	intervalDisplay(INTERVAL_SHORT[interval] + (units && units > 1 ? 's' : ''), units);

export const generateUniqueTransactionRef = () => {
	return moment().format('YYYYMMDD-HHmmss-') + nanoid(5);
};

export const generateReceiptNo = () => {
	return Math.floor(Math.random() * 99999999)
		.toString()
		.padStart(6, '0');
};

export function getAllowedCurrencies(commonConfig: string | undefined, disableAudFallback?: boolean) {
	const common = tryParseJSONObject<FinancialConfigCommon>(commonConfig);

	//Default to AUD if there are no tenant settings
	let currency = 'AUD';

	const allowedCurrencies: string[] = common?.allowedCurrencies || [];
	const defaultCurrency = common?.defaults?.currency;

	if (!defaultCurrency && !allowedCurrencies.length && disableAudFallback) return undefined;

	//Determine the default currency based on available settings
	if (defaultCurrency) {
		currency = defaultCurrency;
	} else if (allowedCurrencies.length > 0) {
		// Default to the first allowed currency if no default is set
		currency = allowedCurrencies[0];
	}

	//Ensure the default currency is included in the list of allowable currencies
	if (!allowedCurrencies.includes(currency)) {
		allowedCurrencies.push(currency);
	}

	return { allowed: allowedCurrencies, default: currency };
}

const DEFAULT_CURRENCY_SYMBOL = '$';

export const currencySymbolDict = {
	CHF: '₣', // Swiss Franc
	EUR: '€', // Euro
	CRC: '₡', // Costa Rican Colón
	GBP: '£', // British Pound Sterling
	ILS: '₪', // Israeli New Sheqel
	INR: '₹', // Indian Rupee
	JPY: '¥', // Japanese Yen
	CNY: '¥', // Chinese Yuan
	KRW: '₩', // South Korean Won
	NGN: '₦', // Nigerian Naira
	PHP: '₱', // Philippine Peso
	PLN: 'zł', // Polish Zloty
	PYG: '₲', // Paraguayan Guarani
	THB: '฿', // Thai Baht
	UAH: '₴', // Ukrainian Hryvnia
	VND: '₫' // Vietnamese Dong
};

// https://en.wikipedia.org/wiki/ISO_4217
const DEFAULT_MINOR_DIGITS = 2;
export const currencyMinorDigits = {
	BHD: 3,
	CLP: 0,
	CLF: 4,
	UYW: 4,
	KWD: 3,
	GNF: 0,
	IQD: 3,
	JPY: 0,
	JOD: 3,
	KMF: 0,
	KRW: 0,
	LYD: 3,
	OMR: 3,
	TND: 3,
	BIF: 0,
	DJF: 0,
	ISK: 0,
	PYG: 0,
	RWF: 0,
	UGX: 0,
	UYI: 0,
	VND: 0,
	VUV: 0,
	XAF: 0,
	XOF: 0,
	XPF: 0
};

/**
 * @deprecated Please replace instances of this function with formatCurrencyFromMinorUnits
 */
export function formatCurrency(baseAmount?: CurrencyMinor | null, currency: string = 'AUD') {
	return formatCurrencyFromMinorUnits(baseAmount ?? null, currency);
}

/**
 * @property cutOffCents (default true)
 * If the amount is a whole dollar, cut off the cents and format as
 * $4 instead of $4.00
 * @property allowFractionalCurrency (default false)
 * If the amount is in fractions of cents (e.g. petrol prices), include the additional
 * decimal places rather than rounding
 */
export type FormatCurrencyOptions = {
	/** If the amount is a whole dollar, cut off the cents and format as $4 instead of
	 * $4.00. Defaults to true.
	 */
	cutOffCents?: boolean;
	/** If the amount is in fractions of cents (e.g. petrol prices), include the additional
	 * decimal places rather than rounding. Defaults to false.
	 */
	allowFractionalCurrency?: boolean;
};

/**
 * Get a number suitable to enter in the CurrencyInput
 */
export function baseCurrencyFromMinorUnits(amount: number | null, currency: string = 'AUD') {
	return formatCurrencyAmount(true, amount, currency, true, false);
}

/** Takes the amount (in cents or equivalent atomic currency unit)
 *  and formats it into a more readable string based on the currency, this will include the currency symbol
 */
export function formatCurrencyFromMinorUnits(
	amount: CurrencyMinor | null,
	currency: string = DEFAULT_CURRENCY_SYMBOL,
	options?: FormatCurrencyOptions
) {
	return `${currencySymbolDict[currency] ?? DEFAULT_CURRENCY_SYMBOL}${formatCurrencyAmount(
		true,
		amount,
		currency,
		options?.cutOffCents ?? true,
		options?.allowFractionalCurrency ?? false
	)}`;
}

/** Takes the amount (in dollars or equivalent main currency unit)
 *  and formats it into a more readable string based on the currency
 */
export function formatCurrencyFromMajorUnits(amount: CurrencyMajor | null, currency: string, options?: FormatCurrencyOptions) {
	return `${currencySymbolDict[currency] ?? DEFAULT_CURRENCY_SYMBOL}${formatCurrencyAmount(
		false,
		amount,
		currency,
		options?.cutOffCents ?? true,
		options?.allowFractionalCurrency ?? false
	)}`;
}

/** Takes an optional currency string and returns the corresponding currency symbol
 *  or the default currency symbol if the currency is undefined or not found
 */
export function getCurrencySymbol(currency?: string) {
	return currency ? (currencySymbolDict[currency] ?? DEFAULT_CURRENCY_SYMBOL) : DEFAULT_CURRENCY_SYMBOL;
}

export function minorFactor(currency?: string): number {
	const digits = currency && typeof currencyMinorDigits[currency] === 'number' ? currencyMinorDigits[currency] : DEFAULT_MINOR_DIGITS;
	return digits;
}

export function formatCurrencyAmount(
	inMinorUnits: boolean,
	amount: number | null,
	currency: string,
	cutOffCents: boolean,
	allowFractionalCurrency: boolean
) {
	const factor = minorFactor(currency);
	const value = inMinorUnits ? (amount ?? 0) / Math.pow(10, factor) : (amount ?? 0);
	if (cutOffCents && Number.isInteger(value)) return value.toFixed(0);
	return value.toLocaleString(undefined, {
		minimumFractionDigits: factor,
		maximumFractionDigits: allowFractionalCurrency ? factor + 4 : factor
	});
}

export function convertMajorToMinor(amount: CurrencyMajor | string | null, currency: string): CurrencyMinor | null {
	const factor = minorFactor(currency);
	return !isNaN(Number(amount)) ? (Math.round(Number(amount) * Math.pow(10, factor)) as CurrencyMinor) : null;
}
